import React from "react";
import { isPhysicsAuthoringLocked } from "../../utils/physicsGuards";

export function useMainPanelMenuPanelSetterFactory({
  isMobilePanelViewport,
  closeMainPanelPopups,
}) {
  const openMobileExclusivePanel = React.useCallback(
    (openPanel) => {
      if (isMobilePanelViewport) {
        closeMainPanelPopups();
        /* Inspector stays on its own channel; do not dispatch close-properties-panel. */
      }
      openPanel();
    },
    [closeMainPanelPopups, isMobilePanelViewport]
  );

  const makeMobileExclusiveSetter = React.useCallback(
    (setOpen) => (open) => {
      if (open) {
        openMobileExclusivePanel(() => setOpen(true));
        return;
      }
      setOpen(false);
    },
    [openMobileExclusivePanel]
  );

  return { openMobileExclusivePanel, makeMobileExclusiveSetter };
}

export function useMainPanelOpenControls({
  isMobilePanelViewport,
  closeMainPanelPopups,
  setSceneGraphOpen,
  setFilePanelOpen,
  setAnimationPanelOpen,
  setMaterialEditorPanelOpen,
  setLightsPanelOpen,
  setCircleDetectionOpen,
  setHistoryPanelOpen,
  setRuntimeDebugOpen,
  setIkTargetPanelOpen,
  setObjectLibraryOpen,
  setRuntimeControlsOpen,
  setPhysicsPanelOpen,
  setActuatorControlsOpen,
  setWarmstartDatasetOpen,
  setBehaviorEditorOpen,
  setPolicyRolloutOpen,
  setPolicyRolloutRequest,
  setTrainingMetricsOpen,
  setTrainingPipelineOpen,
  setOctoTrainingOpen,
  setMeshEditorOpen,
  setSceneInventoryOpen,
  setModelCheckOpen,
  setMassPropertiesOpen,
  setMeasurementsOpen,
  setVisibilitySettingsOpen,
  setTerrainPanelOpen,
  setXmlSourcePanelOpen,
  setCameraProbeOpen,
  setSensorsOpen,
  setShortcutsPanelOpen,
  setGetUpdatesPanelOpen,
  setSendFeedbackPanelOpen,
  setAboutPanelOpen,
  setGremlinsPanelOpen,
}) {
  const { openMobileExclusivePanel, makeMobileExclusiveSetter } =
    useMainPanelMenuPanelSetterFactory({
      isMobilePanelViewport,
      closeMainPanelPopups,
    });

  const setSceneGraphOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSceneGraphOpen),
    [makeMobileExclusiveSetter, setSceneGraphOpen]
  );
  const setFilePanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setFilePanelOpen),
    [makeMobileExclusiveSetter, setFilePanelOpen]
  );
  const setAnimationPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setAnimationPanelOpen),
    [makeMobileExclusiveSetter, setAnimationPanelOpen]
  );
  const setMaterialEditorPanelOpenFromMenu = React.useMemo(
    () =>
      makeMobileExclusiveSetter((open) => {
        if (open && isPhysicsAuthoringLocked()) return;
        setMaterialEditorPanelOpen(open);
      }),
    [makeMobileExclusiveSetter, setMaterialEditorPanelOpen]
  );
  const setLightsPanelOpenFromMenu = React.useMemo(
    () =>
      makeMobileExclusiveSetter((open) => {
        if (open && isPhysicsAuthoringLocked()) return;
        setLightsPanelOpen(open);
      }),
    [makeMobileExclusiveSetter, setLightsPanelOpen]
  );
  const setCircleDetectionOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setCircleDetectionOpen),
    [makeMobileExclusiveSetter, setCircleDetectionOpen]
  );
  const setHistoryPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setHistoryPanelOpen),
    [makeMobileExclusiveSetter, setHistoryPanelOpen]
  );
  const setRuntimeDebugOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setRuntimeDebugOpen),
    [makeMobileExclusiveSetter, setRuntimeDebugOpen]
  );
  const setIkTargetPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setIkTargetPanelOpen),
    [makeMobileExclusiveSetter, setIkTargetPanelOpen]
  );
  const setObjectLibraryOpenFromMenu = React.useMemo(
    () =>
      makeMobileExclusiveSetter((open) => {
        if (open && isPhysicsAuthoringLocked()) return;
        setObjectLibraryOpen(open);
      }),
    [makeMobileExclusiveSetter, setObjectLibraryOpen]
  );
  const setRuntimeControlsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setRuntimeControlsOpen),
    [makeMobileExclusiveSetter, setRuntimeControlsOpen]
  );
  const setActuatorControlsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setActuatorControlsOpen),
    [makeMobileExclusiveSetter, setActuatorControlsOpen]
  );
  const setWarmstartDatasetOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setWarmstartDatasetOpen),
    [makeMobileExclusiveSetter, setWarmstartDatasetOpen]
  );
  const setBehaviorEditorOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setBehaviorEditorOpen),
    [makeMobileExclusiveSetter, setBehaviorEditorOpen]
  );
  const setTrainingPipelineOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setTrainingPipelineOpen),
    [makeMobileExclusiveSetter, setTrainingPipelineOpen]
  );
  const setOctoTrainingOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setOctoTrainingOpen),
    [makeMobileExclusiveSetter, setOctoTrainingOpen]
  );
  const setTrainingMetricsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setTrainingMetricsOpen),
    [makeMobileExclusiveSetter, setTrainingMetricsOpen]
  );
  const setPolicyRolloutOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setPolicyRolloutOpen),
    [makeMobileExclusiveSetter, setPolicyRolloutOpen]
  );
  const setCameraProbeOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setCameraProbeOpen),
    [makeMobileExclusiveSetter, setCameraProbeOpen]
  );
  const setSensorsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSensorsOpen),
    [makeMobileExclusiveSetter, setSensorsOpen]
  );
  const setMeshEditorOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMeshEditorOpen),
    [makeMobileExclusiveSetter, setMeshEditorOpen]
  );
  const setSceneInventoryOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSceneInventoryOpen),
    [makeMobileExclusiveSetter, setSceneInventoryOpen]
  );
  const setModelCheckOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setModelCheckOpen),
    [makeMobileExclusiveSetter, setModelCheckOpen]
  );
  const setMassPropertiesOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMassPropertiesOpen),
    [makeMobileExclusiveSetter, setMassPropertiesOpen]
  );
  const setMeasurementsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setMeasurementsOpen),
    [makeMobileExclusiveSetter, setMeasurementsOpen]
  );
  const setVisibilitySettingsOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setVisibilitySettingsOpen),
    [makeMobileExclusiveSetter, setVisibilitySettingsOpen]
  );
  const setTerrainPanelOpenFromMenu = React.useMemo(
    () =>
      makeMobileExclusiveSetter((open) => {
        if (open && isPhysicsAuthoringLocked()) return;
        setTerrainPanelOpen(open);
      }),
    [makeMobileExclusiveSetter, setTerrainPanelOpen]
  );
  const setXmlSourcePanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setXmlSourcePanelOpen),
    [makeMobileExclusiveSetter, setXmlSourcePanelOpen]
  );
  const setGremlinsPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setGremlinsPanelOpen),
    [makeMobileExclusiveSetter, setGremlinsPanelOpen]
  );
  const setShortcutsPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setShortcutsPanelOpen),
    [makeMobileExclusiveSetter, setShortcutsPanelOpen]
  );
  const setGetUpdatesPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setGetUpdatesPanelOpen),
    [makeMobileExclusiveSetter, setGetUpdatesPanelOpen]
  );
  const setSendFeedbackPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setSendFeedbackPanelOpen),
    [makeMobileExclusiveSetter, setSendFeedbackPanelOpen]
  );
  const setAboutPanelOpenFromMenu = React.useMemo(
    () => makeMobileExclusiveSetter(setAboutPanelOpen),
    [makeMobileExclusiveSetter, setAboutPanelOpen]
  );

  const openPhysicsPanel = React.useCallback(() => {
    if (isPhysicsAuthoringLocked()) return;
    openMobileExclusivePanel(() => setPhysicsPanelOpen(true));
  }, [openMobileExclusivePanel, setPhysicsPanelOpen]);
  const openRuntimeControlsPanel = React.useCallback(() => {
    openMobileExclusivePanel(() => setRuntimeControlsOpen(true));
  }, [openMobileExclusivePanel, setRuntimeControlsOpen]);
  const openTrainingMetricsPanel = React.useCallback(() => {
    openMobileExclusivePanel(() => setTrainingMetricsOpen(true));
  }, [openMobileExclusivePanel, setTrainingMetricsOpen]);
  const openPolicyRolloutPanel = React.useCallback(
    (request = null) => {
      if (request && typeof request === "object") {
        setPolicyRolloutRequest({ ...request, requestedAt: Date.now() });
      }
      openMobileExclusivePanel(() => setPolicyRolloutOpen(true));
    },
    [openMobileExclusivePanel, setPolicyRolloutOpen, setPolicyRolloutRequest]
  );
  const openWarmstartDatasetPanel = React.useCallback(() => {
    openMobileExclusivePanel(() => setWarmstartDatasetOpen(true));
  }, [openMobileExclusivePanel, setWarmstartDatasetOpen]);
  const openOctoTrainingPanel = React.useCallback(() => {
    openMobileExclusivePanel(() => setOctoTrainingOpen(true));
  }, [openMobileExclusivePanel, setOctoTrainingOpen]);

  const closeObjectLibraryPanel = React.useCallback(
    () => setObjectLibraryOpen(false),
    [setObjectLibraryOpen]
  );
  const closeSceneGraph = React.useCallback(() => setSceneGraphOpen(false), [setSceneGraphOpen]);
  const closeFilePanel = React.useCallback(() => setFilePanelOpen(false), [setFilePanelOpen]);
  const closeAnimationPanel = React.useCallback(
    () => setAnimationPanelOpen(false),
    [setAnimationPanelOpen]
  );
  const closeMaterialEditorPanel = React.useCallback(
    () => setMaterialEditorPanelOpen(false),
    [setMaterialEditorPanelOpen]
  );
  const closeLightsPanel = React.useCallback(() => setLightsPanelOpen(false), [setLightsPanelOpen]);
  const closeIkTargetPanel = React.useCallback(
    () => setIkTargetPanelOpen(false),
    [setIkTargetPanelOpen]
  );
  const closePhysicsPanel = React.useCallback(
    () => setPhysicsPanelOpen(false),
    [setPhysicsPanelOpen]
  );
  const closeRuntimeControlsPanel = React.useCallback(
    () => setRuntimeControlsOpen(false),
    [setRuntimeControlsOpen]
  );
  const closeActuatorControlsPanel = React.useCallback(
    () => setActuatorControlsOpen(false),
    [setActuatorControlsOpen]
  );
  const closeWarmstartDatasetPanel = React.useCallback(
    () => setWarmstartDatasetOpen(false),
    [setWarmstartDatasetOpen]
  );
  const closeBehaviorEditorPanel = React.useCallback(
    () => setBehaviorEditorOpen(false),
    [setBehaviorEditorOpen]
  );
  const closeTrainingPipelinePanel = React.useCallback(
    () => setTrainingPipelineOpen(false),
    [setTrainingPipelineOpen]
  );
  const closeOctoTrainingPanel = React.useCallback(
    () => setOctoTrainingOpen(false),
    [setOctoTrainingOpen]
  );
  const closeTrainingMetricsPanel = React.useCallback(
    () => setTrainingMetricsOpen(false),
    [setTrainingMetricsOpen]
  );
  const closePolicyRolloutPanel = React.useCallback(
    () => setPolicyRolloutOpen(false),
    [setPolicyRolloutOpen]
  );
  const closeCameraProbePanel = React.useCallback(
    () => setCameraProbeOpen(false),
    [setCameraProbeOpen]
  );
  const closeSensorsPanel = React.useCallback(() => setSensorsOpen(false), [setSensorsOpen]);
  const closeShortcutsPanel = React.useCallback(
    () => setShortcutsPanelOpen(false),
    [setShortcutsPanelOpen]
  );
  const closeGetUpdatesPanel = React.useCallback(
    () => setGetUpdatesPanelOpen(false),
    [setGetUpdatesPanelOpen]
  );
  const closeSendFeedbackPanel = React.useCallback(
    () => setSendFeedbackPanelOpen(false),
    [setSendFeedbackPanelOpen]
  );
  const closeAboutPanel = React.useCallback(() => setAboutPanelOpen(false), [setAboutPanelOpen]);
  const closeGremlinsPanel = React.useCallback(
    () => setGremlinsPanelOpen(false),
    [setGremlinsPanelOpen]
  );
  const closeCircleDetectionPanel = React.useCallback(
    () => setCircleDetectionOpen(false),
    [setCircleDetectionOpen]
  );
  const closeHistoryPanel = React.useCallback(
    () => setHistoryPanelOpen(false),
    [setHistoryPanelOpen]
  );
  const closeXmlSourcePanel = React.useCallback(
    () => setXmlSourcePanelOpen(false),
    [setXmlSourcePanelOpen]
  );
  const closeRuntimeDebugPanel = React.useCallback(
    () => setRuntimeDebugOpen(false),
    [setRuntimeDebugOpen]
  );
  const closeMeshEditorPanel = React.useCallback(
    () => setMeshEditorOpen(false),
    [setMeshEditorOpen]
  );
  const closeSceneInventory = React.useCallback(
    () => setSceneInventoryOpen(false),
    [setSceneInventoryOpen]
  );
  const closeModelCheckPanel = React.useCallback(
    () => setModelCheckOpen(false),
    [setModelCheckOpen]
  );
  const closeMassPropertiesPanel = React.useCallback(
    () => setMassPropertiesOpen(false),
    [setMassPropertiesOpen]
  );
  const closeMeasurementsPanel = React.useCallback(
    () => setMeasurementsOpen(false),
    [setMeasurementsOpen]
  );
  const closeVisibilitySettings = React.useCallback(
    () => setVisibilitySettingsOpen(false),
    [setVisibilitySettingsOpen]
  );
  const closeTerrainPanel = React.useCallback(
    () => setTerrainPanelOpen(false),
    [setTerrainPanelOpen]
  );

  return {
    setSceneGraphOpenFromMenu,
    setFilePanelOpenFromMenu,
    setAnimationPanelOpenFromMenu,
    setMaterialEditorPanelOpenFromMenu,
    setLightsPanelOpenFromMenu,
    setCircleDetectionOpenFromMenu,
    setHistoryPanelOpenFromMenu,
    setRuntimeDebugOpenFromMenu,
    setIkTargetPanelOpenFromMenu,
    setObjectLibraryOpenFromMenu,
    setRuntimeControlsOpenFromMenu,
    setActuatorControlsOpenFromMenu,
    setWarmstartDatasetOpenFromMenu,
    setBehaviorEditorOpenFromMenu,
    setTrainingPipelineOpenFromMenu,
    setOctoTrainingOpenFromMenu,
    setTrainingMetricsOpenFromMenu,
    setPolicyRolloutOpenFromMenu,
    setCameraProbeOpenFromMenu,
    setSensorsOpenFromMenu,
    setMeshEditorOpenFromMenu,
    setSceneInventoryOpenFromMenu,
    setModelCheckOpenFromMenu,
    setMassPropertiesOpenFromMenu,
    setMeasurementsOpenFromMenu,
    setVisibilitySettingsOpenFromMenu,
    setTerrainPanelOpenFromMenu,
    setXmlSourcePanelOpenFromMenu,
    setGremlinsPanelOpenFromMenu,
    setShortcutsPanelOpenFromMenu,
    setGetUpdatesPanelOpenFromMenu,
    setSendFeedbackPanelOpenFromMenu,
    setAboutPanelOpenFromMenu,
    openPhysicsPanel,
    openRuntimeControlsPanel,
    openTrainingMetricsPanel,
    openPolicyRolloutPanel,
    openWarmstartDatasetPanel,
    openOctoTrainingPanel,
    closeObjectLibraryPanel,
    closeSceneGraph,
    closeFilePanel,
    closeAnimationPanel,
    closeMaterialEditorPanel,
    closeLightsPanel,
    closeIkTargetPanel,
    closePhysicsPanel,
    closeRuntimeControlsPanel,
    closeActuatorControlsPanel,
    closeWarmstartDatasetPanel,
    closeBehaviorEditorPanel,
    closeTrainingPipelinePanel,
    closeOctoTrainingPanel,
    closeTrainingMetricsPanel,
    closePolicyRolloutPanel,
    closeCameraProbePanel,
    closeSensorsPanel,
    closeShortcutsPanel,
    closeGetUpdatesPanel,
    closeSendFeedbackPanel,
    closeAboutPanel,
    closeGremlinsPanel,
    closeCircleDetectionPanel,
    closeHistoryPanel,
    closeXmlSourcePanel,
    closeRuntimeDebugPanel,
    closeMeshEditorPanel,
    closeSceneInventory,
    closeModelCheckPanel,
    closeMassPropertiesPanel,
    closeMeasurementsPanel,
    closeVisibilitySettings,
    closeTerrainPanel,
  };
}
